import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import { taskService } from '@services/taskService';
import toast from 'react-hot-toast';

interface TaskRow {
  name: string;
  leave: boolean;
  overhead: boolean;
  isNew?: boolean;
}

interface AllTasksModalProps {
  show: boolean;
  onHide: () => void;
  onSave: () => void;
}

const AllTasksModal: FC<AllTasksModalProps> = ({ show, onHide, onSave }) => {
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) {
      loadTasks();
    }
  }, [show]);

  const loadTasks = async () => {
    try {
      const allTasks = await taskService.getAllTasks();
      setTasks(
        allTasks.map((task) => ({
          name: task.name.S,
          leave: task.leave.BOOL,
          overhead: task.overhead.BOOL,
        }))
      );
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Failed to load tasks');
    }
  };

  const handleAddTask = () => {
    setTasks([...tasks, { name: '', leave: false, overhead: false, isNew: true }]);
  };

  const handleRemoveTask = (index: number) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const handleTaskChange = (index: number, field: keyof TaskRow, value: any) => {
    const updatedTasks = [...tasks];
    updatedTasks[index] = { ...updatedTasks[index], [field]: value };
    setTasks(updatedTasks);
  };

  const handleSave = async () => {
    // Validate that all tasks have names
    const hasEmptyNames = tasks.some((task) => !task.name.trim());
    if (hasEmptyNames) {
      toast.error('All tasks must have a name');
      return;
    }

    // Check for duplicate names
    const names = tasks.map((task) => task.name.trim());
    const uniqueNames = new Set(names);
    if (names.length !== uniqueNames.size) {
      toast.error('Task names must be unique');
      return;
    }

    setIsSaving(true);
    try {
      await taskService.saveAllTasks(
        tasks.map((task) => ({
          name: task.name.trim(),
          leave: task.leave,
          overhead: task.overhead,
        }))
      );
      toast.success('Tasks saved successfully');
      onSave();
      onHide();
    } catch (error) {
      console.error('Error saving tasks:', error);
      toast.error('Failed to save tasks');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Manage All Tasks</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table striped bordered>
          <thead>
            <tr>
              <th>Task Name</th>
              <th>Leave</th>
              <th>Overhead</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task, index) => (
              <tr key={index}>
                <td>
                  <Form.Control
                    type="text"
                    value={task.name}
                    onChange={(e) => handleTaskChange(index, 'name', e.target.value)}
                    placeholder="Task name"
                  />
                </td>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={task.leave}
                    onChange={(e) => handleTaskChange(index, 'leave', e.target.checked)}
                  />
                </td>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={task.overhead}
                    onChange={(e) => handleTaskChange(index, 'overhead', e.target.checked)}
                  />
                </td>
                <td className="text-center">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveTask(index)}
                  >
                    <i className="bi bi-trash"></i>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button variant="link" onClick={handleAddTask} className="p-0">
          <i className="bi bi-plus-circle"></i> Add Task
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AllTasksModal;
